import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  PieChart, Users, ArrowUpRight, Gift, Percent, UserPlus,
} from 'lucide-react';

/**
 * Three distribution cards + program toggles for Admin Overview.
 * Extracted from AdminOverviewTab.jsx (iter123 refactor).
 */
export const AdminOverviewDistribution = ({ users, programs, programSettings, savingProgram, onToggleProgram }) => (
  <>
    {/* Distribution Charts */}
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-900 text-base flex items-center gap-2">
            <PieChart className="w-4 h-4 text-purple-600" />
            Users by Tier
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(users.by_tier || {}).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    tier === 'free' ? 'bg-slate-400' :
                    tier === 'starter' ? 'bg-blue-400' :
                    tier === 'professional' ? 'bg-purple-400' :
                    tier === 'enterprise' ? 'bg-amber-400' : 'bg-emerald-400'
                  }`} />
                  <span className="text-sm text-slate-600 capitalize">{tier}</span>
                </div>
                <span className="font-medium text-slate-900">{count}</span>
              </div>
            ))}
            {Object.keys(users.by_tier || {}).length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No data available</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-900 text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            Users by Role
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(users.by_role || {}).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    role === 'system_admin' ? 'bg-red-400' :
                    role === 'reseller' ? 'bg-purple-400' :
                    role === 'moderator' ? 'bg-amber-400' : 'bg-blue-400'
                  }`} />
                  <span className="text-sm text-slate-600 capitalize">{role.replace('_', ' ')}</span>
                </div>
                <span className="font-medium text-slate-900">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-900 text-base flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-emerald-600" />
            Conversion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-4xl font-bold text-emerald-600">{users.conversion_rate || 0}%</p>
            <p className="text-sm text-slate-500 mt-1">Free to Paid</p>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Reseller Referrals</span>
              <span className="font-medium text-slate-900">{programs.reseller_referrals || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Affiliate Referrals</span>
              <span className="font-medium text-slate-900">{programs.affiliate_referrals || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Program Toggles */}
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-purple-600" />
          Revenue Programs
        </CardTitle>
        <CardDescription className="text-slate-600">
          Enable or disable reseller and affiliate programs
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className={`p-4 rounded-lg border-2 ${programSettings?.reseller?.enabled ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-slate-900">Reseller Program</span>
              </div>
              <Switch
                checked={programSettings?.reseller?.enabled ?? true}
                onCheckedChange={(checked) => onToggleProgram('reseller', checked)}
                disabled={savingProgram}
              />
            </div>
            <p className="text-sm text-slate-600 mb-2">
              Allow dedicated resellers to earn commissions by bringing customers.
            </p>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Active Resellers:</span>
              <span className="font-medium text-slate-900">{programs.reseller_count || 0}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-500">Default Commission:</span>
              <span className="font-medium text-slate-900">{programSettings?.reseller?.default_commission_rate || 20}%</span>
            </div>
          </div>

          <div className={`p-4 rounded-lg border-2 ${programSettings?.affiliate?.enabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-slate-900">Affiliate Program</span>
              </div>
              <Switch
                checked={programSettings?.affiliate?.enabled ?? true}
                onCheckedChange={(checked) => onToggleProgram('affiliate', checked)}
                disabled={savingProgram}
              />
            </div>
            <p className="text-sm text-slate-600 mb-2">
              Allow ALL users to earn commissions by referring others (tiered rates).
            </p>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Total Referrals:</span>
              <span className="font-medium text-slate-900">{programs.affiliate_referrals || 0}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-500">Commission Tiers:</span>
              <span className="font-medium text-slate-900">Bronze → Platinum (10-25%)</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </>
);

export default AdminOverviewDistribution;
